/**
 * PDP Variant Metafield Updater
 *
 * Smoothly updates variant-specific metafield content when the COLOR changes.
 * Size-only changes are ignored — already handled by product-info.js (price/availability).
 *
 * Piggybacks on the HTML already fetched by product-info.js via PUB_SUB_EVENTS.variantChange,
 * so no additional network request is made.
 */
(function () {
  const FADE_DURATION = 80;
  let pendingUpdate = null;

  // Sections that change per-COLOUR and sit above the fold. These are swapped
  // INSTANTLY from pre-rendered <template>s (see #pdp-color-meta-templates in
  // main-product.liquid) on optionValueSelectionChange — no network wait, no blink.
  // They are therefore EXCLUDED from the network-gated updateMetafieldSections() swap.
  const INSTANT_META_KEYS = ["product-label", "product-rating"];

  // Swap the live label + rating to the given colour's values, sourced from the
  // pre-rendered templates. Only writes when the content actually differs, so an
  // unchanged value never flashes. Also forces opacity:1 to clear the
  // data-pdp-pending-color first-paint guard for these two sections.
  function applyColorMeta(colorKey) {
    colorKey = (colorKey || "").toLowerCase().trim();
    const root = document.getElementById("pdp-color-meta-templates");

    INSTANT_META_KEYS.forEach((key) => {
      const live = document.querySelector(`[data-pdp-section="${key}"]`);
      if (!live) return;
      // Reveal regardless of whether a matching template exists (server already
      // rendered the correct value for the current colour).
      live.style.opacity = "1";
      if (!root || !colorKey) return;
      const tpl = Array.from(
        root.querySelectorAll(`template[data-meta="${key}"]`),
      ).find(
        (t) => (t.dataset.metaColor || "").toLowerCase().trim() === colorKey,
      );
      if (!tpl) return;
      const newHtml = tpl.innerHTML;
      if (live.innerHTML.trim() === newHtml.trim()) return; // no-op → no flash
      live.innerHTML = newHtml;
    });
  }

  // Resolve the colour to render on first paint: URL ?color= if present
  // (deep-link), otherwise the server-rendered colour from #pdp-color-key.
  function getInitialColor() {
    try {
      const keyEl = document.querySelector("#pdp-color-key");
      const colorParam = keyEl?.dataset.colorOptionKey || "color";
      const urlColor = new URLSearchParams(window.location.search).get(
        colorParam,
      );
      if (urlColor) return urlColor.toLowerCase().replace(/-/g, " ").trim();
      return (keyEl?.dataset.colorKey || "").toLowerCase().trim();
    } catch (e) {
      return "";
    }
  }

  function init() {
    if (
      typeof subscribe === "undefined" ||
      typeof PUB_SUB_EVENTS === "undefined"
    ) {
      requestAnimationFrame(init);
      return;
    }

    // Handle initial page-load color mismatch (URL ?color= differs from server-rendered color).
    if (document.documentElement.hasAttribute("data-pdp-pending-color")) {
      const urlColor = document.documentElement
        .getAttribute("data-pdp-pending-color")
        .replace(/-/g, " ");
      const selectedVariantEl = document.querySelector(
        "[data-selected-variant]",
      );
      let serverColor = "";
      if (selectedVariantEl) {
        try {
          const v = JSON.parse(selectedVariantEl.innerHTML);
          serverColor = (v?.option1 || "").toLowerCase();
        } catch (e) {}
      }
      if (urlColor === serverColor) {
        document.documentElement.removeAttribute("data-pdp-pending-color");
        document.querySelectorAll("[data-pdp-section]").forEach((el) => {
          el.style.transition = `opacity ${FADE_DURATION}ms ease`;
          el.style.opacity = "1";
        });
      }
    }

    // Track the currently-rendered color so size-only changes don't trigger a content swap.
    // Read from #pdp-color-key — this element lives directly in main-product.liquid where
    // _color_variant is in scope. [data-selected-variant] lives inside a render snippet
    // and cannot access _color_variant, so we never read from that.
    let renderedColor =
      (document.querySelector("#pdp-color-key")?.dataset.colorKey || "")
        .trim()
        .toLowerCase() || null;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { html, variant } }) => {
      if (!variant || !html) return;

      // Use the selected variant's option1 (the color the user clicked) for change detection.
      // We can't read from html's #pdp-color-key because when OOS, html is the fallback
      // color's HTML — which would cause false "same color" matches and skip the update.
      const fetchedColor =
        (variant?.option1 || "").trim().toLowerCase() || null;

      // Same color AND in-stock → size-only change, skip content swap.
      // OOS same-color still needs to run so the fallback content (Carbon Silk) is swapped in.
      if (
        fetchedColor &&
        renderedColor &&
        fetchedColor === renderedColor &&
        variant.available !== false
      )
        return;

      renderedColor = fetchedColor || renderedColor;
      updateMetafieldSections(html);
    });

    // Instant label + rating swap on colour tap — fires synchronously (no network),
    // in the same frame the gallery filters, so they no longer lag/blink behind it.
    // Accept any of the known colour-option name prefixes (mirrors how the gallery
    // detects a colour change; also tolerates the "colour" spelling).
    const colorPrefixes = [];
    const galleryName = (
      document.querySelector("pdp-gallery")?.dataset.colorOptionName || ""
    )
      .toLowerCase()
      .trim();
    const keyName = (
      document.querySelector("#pdp-color-key")?.dataset.colorOptionKey || ""
    )
      .toLowerCase()
      .trim();
    if (galleryName) colorPrefixes.push(galleryName + "-");
    if (keyName) colorPrefixes.push(keyName + "-");
    if (!colorPrefixes.length) colorPrefixes.push("color-", "colour-");

    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
      const target = data?.target;
      if (!target) return;
      const inputEl =
        target.tagName === "OPTION" ? target.closest("select") : target;
      const inputName = (inputEl?.name || "").toLowerCase();
      if (!colorPrefixes.some((p) => inputName.startsWith(p))) return; // colour inputs only
      applyColorMeta((target.value || "").toLowerCase().trim());
    });

    // First paint / deep-link: ensure label + rating match the rendered colour
    // and clear the pending-colour guard for them.
    applyColorMeta(getInitialColor());
  }

  function updateMetafieldSections(html) {
    // Cancel any in-flight update
    if (pendingUpdate) {
      clearTimeout(pendingUpdate);
      pendingUpdate = null;
    }

    // Clear the initial page-load color-correction guard if it's still set.
    document.documentElement.removeAttribute("data-pdp-pending-color");

    // Label + rating are handled instantly via applyColorMeta() — exclude them here
    // so the network path doesn't re-blink them after they've already updated.
    const sections = Array.from(
      document.querySelectorAll("[data-pdp-section]"),
    ).filter((el) => !INSTANT_META_KEYS.includes(el.dataset.pdpSection));

    // (B) Scroll-anchor: remember where the swapped region sits relative to the
    // viewport BEFORE we touch anything. Content height differs per colour, so a
    // straight innerHTML replace shifts everything below the change and the page
    // visibly jumps. We re-pin the viewport to this anchor after the swap.
    // Anchor = the swapped container nearest the top of the viewport — its box
    // survives the swap (only its innerHTML is replaced), so its position is a
    // reliable reference for how far the layout moved.
    let anchorEl = null;
    for (const el of sections) {
      anchorEl = el;
      if (el.getBoundingClientRect().top >= 0) break;
    }
    const anchorTopBefore = anchorEl
      ? anchorEl.getBoundingClientRect().top
      : 0;

    // Swap in place at full opacity — no fade. The innerHTML swap, the Swiper
    // re-init and the scroll re-pin below all run synchronously, before the
    // browser's next paint, so the section never renders blank: the new content
    // appears in a single frame with no blink. (The previous version set
    // opacity:0 here and faded back to 1 on the next frame, which forced a blank
    // first paint — that was the remaining flicker.)
    // (A) Sections whose content is identical for this colour are skipped, so an
    // unchanged section isn't touched at all.
    sections.forEach((el) => {
      const name = el.dataset.pdpSection;
      const newEl = html.querySelector(`[data-pdp-section="${name}"]`);
      if (!newEl) return;
      if (el.innerHTML.trim() === newEl.innerHTML.trim()) return; // unchanged → no-op
      el.style.transition = "none";
      el.style.opacity = "1"; // clear any stale inline opacity from older swaps
      el.innerHTML = newEl.innerHTML;
    });

    // Re-init any Swipers that live inside updated sections
    reinitSwipers();

    // (B) Re-pin: now that the new content has reflowed, shift the scroll
    // position by however much the anchor moved so the viewport stays put.
    if (anchorEl) {
      const delta = anchorEl.getBoundingClientRect().top - anchorTopBefore;
      if (delta) window.scrollBy(0, delta);
    }

    // Notify other scripts that sections have been re-rendered
    document.dispatchEvent(new CustomEvent("pdp:sectionsUpdated"));
  }

  function reinitSwipers() {
    // Rebind hydrate section tabs (direct bindings are lost after innerHTML replacement)
    $(".hydrate-section__item")
      .off("click.pdpVariant")
      .on("click.pdpVariant", function () {
        const data_target_h = $(this).attr("data-target");
        $(".hydrate-section__item").removeClass(
          "hydrate-section__item--active",
        );
        $(this).addClass("hydrate-section__item--active");
        $(".hydrate-img").removeClass("hydrate-img--active");
        $('.hydrate-img[data-img="' + data_target_h + '"]').addClass(
          "hydrate-img--active",
        );
      });

    // Rebind result tabs (event delegation on .tab_img_change parent).
    // The switch itself lives in window.pdpTabSwitch (function.js, loaded
    // earlier in the head) — it holds the crossfade until the incoming
    // tab's image is decoded so the first click-through never flashes.
    $(".tab_img_change")
      .off("click.pdpVariant")
      .on("click.pdpVariant", ".tab_btn", function () {
        const $section = $(this).closest(".tab_img_change");
        const tab = $(this).data("tab");
        const text = $(this).data("text");
        if (!tab) return;
        window.pdpTabSwitch($section, $(this), tab, text);
      })
      .on("click.pdpVariant", ".clinical_additional_image", function () {
        const $section = $(this).closest(".tab_img_change");
        const $inactiveBtn = $section.find(".tab_btn:not(.active)");
        if ($inactiveBtn.length) $inactiveBtn.click();
      });

    // Re-init number slides Swiper (only if slides exist in DOM)
    window.swiperInstances = window.swiperInstances || {};
    if (document.querySelector(".productSwiper .swiper-slide")) {
      const prevPS = window.swiperInstances.productSwiper;
      if (prevPS && typeof prevPS.destroy === "function")
        prevPS.destroy(true, true);
      window.swiperInstances.productSwiper = new Swiper(".productSwiper", {
        direction: "horizontal",
        loop: false,
        speed: 800,
        effect: "fade",
        fadeEffect: { crossFade: true },
        navigation: { enabled: false },
        keyboard: { enabled: true },
        mousewheel: { enabled: false },
        on: {
          slideChange: function () {
            updateProductPagination(this.activeIndex);
          },
          init: function () {
            updateProductPagination(this.activeIndex);
          },
        },
      });
      $(".pagination-bullet")
        .off("click.pdpVariant")
        .on("click.pdpVariant", function () {
          const slideIndex = parseInt($(this).attr("data-slide"));
          window.swiperInstances.productSwiper.slideTo(slideIndex);
        });
    }

    function updateProductPagination(activeIndex) {
      $(".pagination-bullet").removeClass("active");
      $(`.pagination-bullet[data-slide="${activeIndex}"]`).addClass("active");
    }

    if (document.querySelector(".ingredients-swiper .swiper-slide")) {
      const prev = window.swiperInstances.ingredients;
      if (prev && typeof prev.destroy === "function") prev.destroy(true, true);
      window.swiperInstances.ingredients = new Swiper(".ingredients-swiper", {
        slidesPerView: 4.5,
        spaceBetween: 30,
        centeredSlides: false,
        loop: false,
        grabCursor: true,
        navigation: {
          nextEl: ".ingredients-button-next",
          prevEl: ".ingredients-button-prev",
        },
        breakpoints: {
          0: {
            slidesPerView: 2.3,
            spaceBetween: typeof vw === "function" ? vw(1.8) : 6,
          },
          768: {
            slidesPerView: 4.5,
            spaceBetween: typeof vw === "function" ? vw(1.7) : 30,
          },
        },
      });
    }

    // Re-init every .five_items_slider (You×Bodiskin + image-carousel). Delegated
    // to the shared initialiser in function.js so a colour-change re-init wires
    // EVERY slider (not just the first match) and stays identical to first paint.
    if (
      document.querySelector(".five_items_slider .swiper-slide") &&
      typeof window.initFiveItemsSliders === "function"
    ) {
      window.initFiveItemsSliders();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
